import { memo, useCallback, useMemo, useState } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Account } from "@common/types/domains/accounting/account";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { useAccounts } from "@hooks/useAccounts";
import { useEntityIdForGLAccount } from "@hooks/useEntityIdForGLAccount";
import { useShowAccountNumbers } from "@hooks/useShowAccountNumbers";
import { useSimilarTransactionsNewValue } from "./SimilarTransactionsNewValueContext";

export const CoaNewValueCell: React.FC<{
    transactionId: number;
    transaction: TransactionListItem;
    accountId: string | null;
    basis?: AccountingLedgerBasis;
}> = memo(
    ({
        transactionId,
        transaction,
        accountId,
        basis = AccountingLedgerBasis.cash,
    }) => {
        const { setNewValue } = useSimilarTransactionsNewValue();
        const entityIdForGLAccount = useEntityIdForGLAccount(transaction);
        const showAccountNumbers = useShowAccountNumbers();
        const [isOpen, setIsOpen] = useState(false);

        const { accounts } = useAccounts({
            entityId: entityIdForGLAccount,
            excludeLinkedToFinancialAccount: true,
            includeCurrentAccount: true,
            currentAccountId: accountId ?? undefined,
            ledgerBasis: basis,
        });

        const selectedAccount = useMemo(
            () => accounts.find((a) => a.id === accountId) ?? undefined,
            [accounts, accountId],
        );

        const handleChange = useCallback(
            (account: Account | null) => {
                setNewValue(transactionId, account?.id ?? null);
                setIsOpen(false);
            },
            [transactionId, setNewValue],
        );

        if (!entityIdForGLAccount) {
            return null;
        }

        return (
            <ViewTableCell captureClicks>
                <AccountCombobox
                    showAccountCode={showAccountNumbers}
                    selectedAccount={selectedAccount}
                    onChange={handleChange}
                    entityId={entityIdForGLAccount}
                    ledgerBasis={basis}
                    open={isOpen}
                    onOpenChange={setIsOpen}
                />
            </ViewTableCell>
        );
    },
);
CoaNewValueCell.displayName = "CoaNewValueCell";
